import React, { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  keyframes,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SpeedIcon from '@mui/icons-material/Speed';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import RefreshIcon from '@mui/icons-material/Refresh';
import PersonIcon from '@mui/icons-material/Person';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import EditIcon from '@mui/icons-material/Edit';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CloseIcon from '@mui/icons-material/Close';
import DescriptionIcon from '@mui/icons-material/Description';
import SendIcon from '@mui/icons-material/Send';
import {
  useUserMeterUsage,
  useSubscriptionPaymentInfo,
  useMeterUsageBillableValueGet,
  useMeterUsageEdit,
  useStripeMeterEvents,
  useInvoicePreviewCreate,
  useMeterUsageReport,
  MeterUsage,
  METER_USAGE_ENTITY_TYPE,
  METER_USAGE_VALUE_TYPE,
  SubscriptionPaymentInfo,
  SubscriptionPrice,
  SubscriptionRenewalData,
  PriceTier,
  StripeMeterEvent,
  StripeInvoice,
} from '../../clients/graphql/pages/meter-usage-queries';

const formatDate = (dateString: string): string => {
  // Extract just the date part to avoid timezone shifting
  const datePart = dateString.split('T')[0];
  const [year, month, day] = datePart.split('-').map(Number);
  // Create date with explicit year/month/day (local timezone)
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString('en-US', {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const formatDateTime = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatDateTimeWithTz = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZoneName: 'short',
  });
};

const formatValue = (value: string): string => {
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
};

const getDateKey = (dateString: string): string => {
  // Extract just the YYYY-MM-DD part without timezone conversion
  return dateString.split('T')[0];
};

type GroupedUsage = {
  date: string;
  records: MeterUsage[];
  totalValue: number;
};

const formatCurrency = (amount: number | null | undefined): string => {
  if (amount == null) return '—';
  return `$${(amount / 100).toFixed(2)}`;
};

const formatYearlyPercent = (value: string | null | undefined): string => {
  if (!value) return '—';
  return `${parseFloat(parseFloat(value).toFixed(3))}%`;
};

const formatEnumValue = (value: string): string => {
  return value
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// Animations for meter loading
const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(0.95);
  }
`;

const rotate = keyframes`
  0% {
    transform: rotate(-20deg);
  }
  50% {
    transform: rotate(20deg);
  }
  100% {
    transform: rotate(-20deg);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
`;

const MeterLoadingAnimation: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 8,
      gap: 3,
    }}
  >
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {/* Outer ring */}
      <Box
        sx={{
          position: 'absolute',
          width: 100,
          height: 100,
          borderRadius: '50%',
          border: '3px solid transparent',
          borderTopColor: '#6366f1',
          borderRightColor: '#6366f1',
          animation: 'spin 1.5s linear infinite',
          '@keyframes spin': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(360deg)' },
          },
        }}
      />
      {/* Middle ring */}
      <Box
        sx={{
          position: 'absolute',
          width: 80,
          height: 80,
          borderRadius: '50%',
          border: '3px solid transparent',
          borderBottomColor: '#a5b4fc',
          borderLeftColor: '#a5b4fc',
          animation: 'spin-reverse 1.2s linear infinite',
          '@keyframes spin-reverse': {
            '0%': { transform: 'rotate(0deg)' },
            '100%': { transform: 'rotate(-360deg)' },
          },
        }}
      />
      {/* Inner background */}
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          backgroundColor: '#f1f5f9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          animation: `${pulse} 2s ease-in-out infinite`,
        }}
      >
        <SpeedIcon
          sx={{
            fontSize: 32,
            color: '#6366f1',
            animation: `${rotate} 1.5s ease-in-out infinite`,
          }}
        />
      </Box>
    </Box>

    {/* Loading text with shimmer */}
    <Box sx={{ textAlign: 'center' }}>
      <Typography
        variant="body1"
        fontWeight={500}
        sx={{
          color: '#475569',
          mb: 0.5,
        }}
      >
        Loading Usage Data
      </Typography>
      <Box
        sx={{
          height: 4,
          width: 120,
          borderRadius: 2,
          background: 'linear-gradient(90deg, #e2e8f0 25%, #6366f1 50%, #e2e8f0 75%)',
          backgroundSize: '200% 100%',
          animation: `${shimmer} 1.5s ease-in-out infinite`,
        }}
      />
    </Box>
  </Box>
);

export const MeteredUsagePage: React.FC = () => {
  const { userKey } = useParams<{ userKey: string }>();
  const navigate = useNavigate();
  const numericUserKey = parseInt(userKey || '0');

  // First, fetch subscription data to get the invoice period dates
  const { data: subscriptionData, loading: subscriptionLoading } = useSubscriptionPaymentInfo(
    numericUserKey,
    { skip: !numericUserKey }
  );

  const subscriptions = subscriptionData?.subscriptionPaymentInfoGet?.subscriptions || [];

  // Find the metered price renewal data to get period dates
  const meteredRenewalData = useMemo(() => {
    for (const subscription of subscriptions) {
      const renewalData = subscription.renewalData?.find(
        (rd: SubscriptionRenewalData) => rd.price?.usageType?.toUpperCase() === 'METERED'
      );
      if (renewalData?.currentPeriodStart && renewalData?.currentPeriodEnd) {
        return renewalData;
      }
    }
    return null;
  }, [subscriptions]);

  // Use period dates for queries, default to current month if not available
  const periodDates = useMemo(() => {
    if (meteredRenewalData?.currentPeriodStart && meteredRenewalData?.currentPeriodEnd) {
      return {
        minDate: meteredRenewalData.currentPeriodStart,
        maxDate: meteredRenewalData.currentPeriodEnd,
      };
    }
    // Fallback to current month
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      minDate: startOfMonth.toISOString(),
      maxDate: endOfMonth.toISOString(),
    };
  }, [meteredRenewalData]);

  // Fetch meter usage with period dates
  const { data, loading, error, refetch } = useUserMeterUsage(
    numericUserKey,
    { minDate: periodDates.minDate, maxDate: periodDates.maxDate },
    { skip: !numericUserKey || subscriptionLoading }
  );

  // Fetch stripe meter events with same period dates
  const { data: meterEventsData, refetch: refetchMeterEvents } = useStripeMeterEvents(
    numericUserKey,
    { minDate: periodDates.minDate, maxDate: periodDates.maxDate },
    { skip: !numericUserKey || subscriptionLoading }
  );

  const stripeMeterEvents: StripeMeterEvent[] = meterEventsData?.whoami?.stripeMeterEvents?.stripeMeterEvents || [];

  // Group meter events by usageForDate (use same getDateKey function as meterUsage for consistency)
  const meterEventsByDate = useMemo(() => {
    const groups: Record<string, StripeMeterEvent[]> = {};
    stripeMeterEvents.forEach((event: StripeMeterEvent) => {
      if (event.usageForDate) {
        const dateKey = getDateKey(event.usageForDate);
        if (!groups[dateKey]) {
          groups[dateKey] = [];
        }
        groups[dateKey].push(event);
      }
    });
    return groups;
  }, [stripeMeterEvents]);

  // State for meter events modal
  const [meterEventsModalOpen, setMeterEventsModalOpen] = useState(false);
  const [selectedDateForEvents, setSelectedDateForEvents] = useState<string | null>(null);
  const [selectedDateEvents, setSelectedDateEvents] = useState<StripeMeterEvent[]>([]);

  // Invoice Preview
  const [invoicePreviewCreate] = useInvoicePreviewCreate(numericUserKey);
  const [invoicePreviews, setInvoicePreviews] = useState<StripeInvoice[]>([]);
  const [invoicePreviewLoading, setInvoicePreviewLoading] = useState(false);

  // Refetch invoice preview function
  const refetchInvoicePreview = async () => {
    const subscriptionIds = subscriptions.map((s: SubscriptionPaymentInfo) => s.subscriptionId).filter(Boolean);
    if (subscriptionIds.length > 0 && numericUserKey) {
      setInvoicePreviewLoading(true);
      try {
        const result = await invoicePreviewCreate({
          variables: {
            input: {
              subscriptionIds,
            },
          },
        });
        setInvoicePreviews(result.data?.invoicePreviewCreate?.invoices || []);
      } catch (err) {
        console.error('Failed to fetch invoice preview:', err);
      } finally {
        setInvoicePreviewLoading(false);
      }
    }
  };

  // Fetch invoice preview when subscriptions are loaded
  useEffect(() => {
    refetchInvoicePreview();
  }, [subscriptions, numericUserKey]);

  // Find subscription with metered price for reporting
  const meteredSubscriptionId = useMemo(() => {
    for (const subscription of subscriptions) {
      const hasMeteredPrice = subscription.products?.some((product: any) =>
        product.prices?.some((price: SubscriptionPrice) => price.usageType?.toUpperCase() === 'METERED')
      );
      if (hasMeteredPrice) {
        return subscription.subscriptionId;
      }
    }
    return null;
  }, [subscriptions]);

  // Report usage mutation
  const [meterUsageReport] = useMeterUsageReport(numericUserKey);
  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);
  const [reportSuccess, setReportSuccess] = useState(false);

  const handleReportUsage = async () => {
    if (!meteredSubscriptionId) return;

    setReportLoading(true);
    setReportError(null);

    try {
      const idempotencyKey = crypto.randomUUID();
      const result = await meterUsageReport({
        variables: {
          input: {
            subscriptionId: meteredSubscriptionId,
            idempotencyKey,
          },
        },
      });

      if (result.data?.meterUsageReport?.success) {
        setReportSuccess(true);
        // Refetch all data after successful report
        await Promise.all([
          refetch(),
          refetchBillableValue(),
          refetchMeterEvents(),
          refetchInvoicePreview(),
        ]);
      } else {
        setReportError('Report failed');
      }
    } catch (err: any) {
      setReportError(err.message || 'Failed to report usage');
    } finally {
      setReportLoading(false);
    }
  };

  const handleCloseReportModal = () => {
    setReportModalOpen(false);
    setReportError(null);
    setReportSuccess(false);
  };

  const handleViewMeterEvents = (date: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const events = meterEventsByDate[date] || [];
    setSelectedDateForEvents(date);
    setSelectedDateEvents(events);
    setMeterEventsModalOpen(true);
  };

  const [getBillableValue] = useMeterUsageBillableValueGet(numericUserKey);
  const [billableValue, setBillableValue] = useState<number | null>(null);
  const [billableValueLoading, setBillableValueLoading] = useState(false);

  // Fetch billable value when we have period dates
  useEffect(() => {
    if (meteredRenewalData?.currentPeriodStart && meteredRenewalData?.currentPeriodEnd && numericUserKey) {
      setBillableValueLoading(true);
      getBillableValue({
        variables: {
          input: {
            type: METER_USAGE_VALUE_TYPE.AUM,
            userKey: numericUserKey,
            periodStart: meteredRenewalData.currentPeriodStart,
            periodEnd: meteredRenewalData.currentPeriodEnd,
          },
        },
      })
        .then((result) => {
          setBillableValue(result.data?.meterUsageBillableValueGet?.billableValue ?? null);
        })
        .catch((err) => {
          console.error('Failed to fetch billable value:', err);
        })
        .finally(() => {
          setBillableValueLoading(false);
        });
    }
  }, [meteredRenewalData, numericUserKey, getBillableValue]);

  // Edit mutation
  const [meterUsageEdit] = useMeterUsageEdit(numericUserKey);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<MeterUsage | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const handleEditClick = (record: MeterUsage) => {
    setEditingRecord(record);
    setEditValue(record.value);
    setEditError(null);
    setEditModalOpen(true);
  };

  const handleEditSubmit = () => {
    setEditModalOpen(false);
    setConfirmModalOpen(true);
  };

  const refetchBillableValue = async () => {
    if (meteredRenewalData?.currentPeriodStart && meteredRenewalData?.currentPeriodEnd && numericUserKey) {
      setBillableValueLoading(true);
      try {
        const result = await getBillableValue({
          variables: {
            input: {
              type: METER_USAGE_VALUE_TYPE.AUM,
              userKey: numericUserKey,
              periodStart: meteredRenewalData.currentPeriodStart,
              periodEnd: meteredRenewalData.currentPeriodEnd,
            },
          },
        });
        setBillableValue(result.data?.meterUsageBillableValueGet?.billableValue ?? null);
      } catch (err) {
        console.error('Failed to fetch billable value:', err);
      } finally {
        setBillableValueLoading(false);
      }
    }
  };

  const handleConfirmEdit = async () => {
    if (!editingRecord) return;

    setEditLoading(true);
    setEditError(null);

    try {
      await meterUsageEdit({
        variables: {
          input: {
            meterUsageKey: editingRecord.meterUsageKey,
            value: editValue,
          },
        },
      });
      setConfirmModalOpen(false);
      setEditingRecord(null);
      setEditValue('');
      // Refetch the data
      await refetch();
      await refetchBillableValue();
    } catch (err: any) {
      setEditError(err.message || 'Failed to update meter usage');
      setConfirmModalOpen(false);
      setEditModalOpen(true);
    } finally {
      setEditLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditModalOpen(false);
    setEditingRecord(null);
    setEditValue('');
    setEditError(null);
  };

  const handleCancelConfirm = () => {
    setConfirmModalOpen(false);
    setEditModalOpen(true);
  };

  const meterUsage: MeterUsage[] = data?.whoami?.meterUsage?.meterUsage || [];

  // Group usage records by day
  const groupedByDay = useMemo((): GroupedUsage[] => {
    const groups: Record<string, MeterUsage[]> = {};

    meterUsage.forEach((record: MeterUsage) => {
      const dateKey = getDateKey(record.date);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(record);
    });

    return Object.entries(groups)
      .map(([date, records]) => ({
        date,
        records: records.sort((a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        ),
        totalValue: records.reduce((sum, r) => sum + parseFloat(r.value || '0'), 0),
      }))
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [meterUsage]);

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <Box sx={{
        backgroundColor: 'white',
        borderBottom: '1px solid #e2e8f0',
        px: 3,
        py: 2.5,
      }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton
              onClick={() => navigate(-1)}
              sx={{
                color: '#64748b',
                '&:hover': { backgroundColor: '#f1f5f9' },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{
              backgroundColor: '#f1f5f9',
              borderRadius: 1.5,
              p: 1,
              display: 'flex',
            }}>
              <SpeedIcon sx={{ color: '#6366f1', fontSize: 24 }} />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={600} color="#1e293b">
                Metered Usage
              </Typography>
              <Typography variant="body2" color="#64748b">
                User {userKey}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Box sx={{ textAlign: 'center', px: 2 }}>
                <Typography variant="h6" fontWeight={600} color="#1e293b">
                  {meterUsage.length}
                </Typography>
                <Typography variant="caption" color="#64748b">
                  Records
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center', px: 2, borderLeft: '1px solid #e2e8f0' }}>
                <Typography variant="h6" fontWeight={600} color="#1e293b">
                  {groupedByDay.length}
                </Typography>
                <Typography variant="caption" color="#64748b">
                  Days
                </Typography>
              </Box>
              {meteredSubscriptionId && (
                <Button
                  variant="contained"
                  startIcon={<SendIcon />}
                  onClick={() => setReportModalOpen(true)}
                  sx={{
                    ml: 1,
                    textTransform: 'none',
                    backgroundColor: '#6366f1',
                    '&:hover': { backgroundColor: '#4f46e5' },
                  }}
                >
                  Report Usage
                </Button>
              )}
              <IconButton
                onClick={() => refetch()}
                sx={{ color: '#64748b' }}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ maxWidth: 1400, mx: 'auto', px: 3, py: 3 }}>
        <Box sx={{ display: 'flex', gap: 3 }}>
          {/* Left Sidebar - Subscription Info */}
          <Box sx={{ width: 340, flexShrink: 0 }}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid #e2e8f0',
                backgroundColor: 'white',
                overflow: 'hidden',
                position: 'sticky',
                top: 16,
              }}
            >
              <Box sx={{
                px: 2.5,
                py: 2,
                borderBottom: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <CreditCardIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                    Subscription Info
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ p: 2 }}>
                {subscriptionLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : subscriptions.length === 0 ? (
                  <Typography variant="body2" color="#64748b" sx={{ py: 2, textAlign: 'center' }}>
                    No active subscriptions
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {subscriptions.map((subscription: SubscriptionPaymentInfo) => (
                      <Box key={subscription.subscriptionId}>
                        {/* Subscription Header */}
                        <Box sx={{ mb: 1.5 }}>
                          <Typography variant="caption" color="#64748b" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                            {subscription.subscriptionId}
                          </Typography>
                          {subscription.renewalText && (
                            <Typography variant="body2" color="#475569" sx={{ mt: 0.5 }}>
                              {subscription.renewalText}
                            </Typography>
                          )}
                        </Box>

                        {/* Products & Prices */}
                        {subscription.products?.map((product, productIdx) => (
                          <Box key={productIdx} sx={{ mb: 1.5 }}>
                            <Typography variant="body2" fontWeight={600} color="#475569" sx={{ mb: 1 }}>
                              {product.name}
                            </Typography>

                            {product.prices?.map((price: SubscriptionPrice) => {
                              const renewalInfo = subscription.renewalData?.find(
                                (rd: SubscriptionRenewalData) => rd.price?.id === price.id
                              );
                              return (
                              <Accordion
                                key={price.id}
                                disableGutters
                                elevation={0}
                                sx={{
                                  border: '1px solid #e2e8f0',
                                  borderRadius: '8px !important',
                                  mb: 1,
                                  '&:before': { display: 'none' },
                                  overflow: 'hidden',
                                }}
                              >
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: '#94a3b8' }} />}
                                  sx={{
                                    minHeight: 44,
                                    px: 1.5,
                                    '& .MuiAccordionSummary-content': { my: 1 },
                                  }}
                                >
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#64748b', fontSize: '0.65rem' }}>
                                        {price.id}
                                      </Typography>
                                      {price.nickname && (
                                        <Typography variant="caption" sx={{ fontWeight: 500, color: '#1e293b', fontSize: '0.7rem' }}>
                                          - {price.nickname}
                                        </Typography>
                                      )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                                      <Chip
                                        label={price.displayInterval || price.interval}
                                        size="small"
                                        sx={{ fontSize: '0.6rem', height: 18, backgroundColor: '#e2e8f0', color: '#475569' }}
                                      />
                                      {price.usageType && (
                                        <Chip
                                          label={formatEnumValue(price.usageType)}
                                          size="small"
                                          sx={{
                                            fontSize: '0.6rem',
                                            height: 18,
                                            backgroundColor: price.usageType.toUpperCase() === 'METERED' ? '#dbeafe' : '#f3e8ff',
                                            color: price.usageType.toUpperCase() === 'METERED' ? '#1e40af' : '#7c3aed',
                                          }}
                                        />
                                      )}
                                    </Box>
                                  </Box>
                                </AccordionSummary>
                                <AccordionDetails sx={{ p: 0, borderTop: '1px solid #e2e8f0' }}>
                                  {/* Renewal Info */}
                                  {renewalInfo && (
                                    <Box sx={{
                                      px: 1.5,
                                      py: 1,
                                      backgroundColor: '#f0fdf4',
                                      borderBottom: '1px solid #e2e8f0',
                                    }}>
                                      {renewalInfo.renewalText && (
                                        <Typography variant="caption" color="#16a34a" fontWeight={500} sx={{ display: 'block', mb: 0.5 }}>
                                          {renewalInfo.renewalText}
                                        </Typography>
                                      )}
                                      {(renewalInfo.currentPeriodStart || renewalInfo.currentPeriodEnd) && (
                                        <Typography variant="caption" color="#64748b" sx={{ fontSize: '0.65rem' }}>
                                          {renewalInfo.currentPeriodStart && formatDate(renewalInfo.currentPeriodStart)}
                                          {renewalInfo.currentPeriodStart && renewalInfo.currentPeriodEnd && ' → '}
                                          {renewalInfo.currentPeriodEnd && formatDate(renewalInfo.currentPeriodEnd)}
                                        </Typography>
                                      )}
                                      {/* Billable Value for Metered Price */}
                                      {price.usageType?.toUpperCase() === 'METERED' && (
                                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #bbf7d0' }}>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="caption" color="#64748b">
                                              Current Billable Value
                                            </Typography>
                                            {billableValueLoading ? (
                                              <CircularProgress size={12} sx={{ color: '#16a34a' }} />
                                            ) : billableValue != null ? (
                                              <Typography variant="caption" fontWeight={600} color="#16a34a">
                                                ${billableValue.toLocaleString()}
                                              </Typography>
                                            ) : (
                                              <Typography variant="caption" color="#94a3b8">—</Typography>
                                            )}
                                          </Box>
                                        </Box>
                                      )}
                                    </Box>
                                  )}

                                  {/* Price Details */}
                                  <Box sx={{ px: 1.5, py: 1, backgroundColor: '#fafafa' }}>
                                    {price.annualAUMFeePercent != null && (
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                        <Typography variant="caption" color="#64748b">Annual AUM Fee</Typography>
                                        <Typography variant="caption" fontWeight={500}>{price.annualAUMFeePercent}%</Typography>
                                      </Box>
                                    )}
                                    {price.unitAmount != null && (
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                        <Typography variant="caption" color="#64748b">Unit Amount</Typography>
                                        <Typography variant="caption" fontWeight={500}>{formatCurrency(price.unitAmount)}</Typography>
                                      </Box>
                                    )}
                                    {price.billingScheme && (
                                      <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                        <Typography variant="caption" color="#64748b">Billing</Typography>
                                        <Typography variant="caption" fontWeight={500}>{formatEnumValue(price.billingScheme)}</Typography>
                                      </Box>
                                    )}
                                  </Box>

                                  {/* Tiers Table */}
                                  {price.tiers && price.tiers.length > 0 && (
                                    <Box sx={{ borderTop: '1px solid #e2e8f0' }}>
                                      <Typography variant="caption" color="#64748b" sx={{ display: 'block', px: 1.5, py: 1, fontWeight: 600 }}>
                                        Pricing Tiers
                                      </Typography>
                                      <Table size="small">
                                        <TableHead>
                                          <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                                            <TableCell sx={{ py: 0.75, fontSize: '0.65rem', fontWeight: 600, color: '#64748b' }}>Up To</TableCell>
                                            <TableCell align="right" sx={{ py: 0.75, fontSize: '0.65rem', fontWeight: 600, color: '#64748b' }}>Yearly %</TableCell>
                                            <TableCell align="right" sx={{ py: 0.75, fontSize: '0.65rem', fontWeight: 600, color: '#64748b' }}>Flat</TableCell>
                                          </TableRow>
                                        </TableHead>
                                        <TableBody>
                                          {price.tiers.map((tier: PriceTier, idx: number) => (
                                            <TableRow key={idx} sx={{ '&:last-child td': { borderBottom: 0 } }}>
                                              <TableCell sx={{ py: 0.5, fontSize: '0.7rem' }}>
                                                {tier.upTo != null ? `$${tier.upTo.toLocaleString()}` : '∞'}
                                              </TableCell>
                                              <TableCell align="right" sx={{ py: 0.5, fontSize: '0.7rem', color: '#059669', fontWeight: 500 }}>
                                                {formatYearlyPercent(tier.yearlyAmountDecimal)}
                                              </TableCell>
                                              <TableCell align="right" sx={{ py: 0.5, fontSize: '0.7rem' }}>
                                                {formatCurrency(tier.flatAmount)}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </Box>
                                  )}
                                </AccordionDetails>
                              </Accordion>
                              );
                            })}
                          </Box>
                        ))}

                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Invoice Preview Section */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: '1px solid #e2e8f0',
                backgroundColor: 'white',
                overflow: 'hidden',
                mt: 2,
              }}
            >
              <Box sx={{
                px: 2.5,
                py: 2,
                borderBottom: '1px solid #e2e8f0',
                backgroundColor: '#f8fafc',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <DescriptionIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                  <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                    Upcoming Invoice
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ p: 2 }}>
                {invoicePreviewLoading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : invoicePreviews.length === 0 ? (
                  <Typography variant="body2" color="#64748b" sx={{ py: 2, textAlign: 'center' }}>
                    No upcoming invoices
                  </Typography>
                ) : (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Invoice Preview Note */}
                    {invoicePreviews[0]?.periodEnd && (
                      <Typography variant="caption" color="#64748b" sx={{ lineHeight: 1.5 }}>
                        This is a preview of the invoice that will be billed on{' '}
                        <strong>{formatDate(invoicePreviews[0].periodEnd)}</strong>. It may change if the subscription is updated.
                        {' '}It may take a few minutes for recent billing meter events to be aggregated and displayed here.
                      </Typography>
                    )}
                    {invoicePreviews.map((invoice: StripeInvoice) => (
                      <Box
                        key={invoice.id}
                        sx={{
                          p: 1.5,
                          backgroundColor: '#f8fafc',
                          borderRadius: 1.5,
                          border: '1px solid #e2e8f0',
                        }}
                      >
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                          <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#64748b', fontSize: '0.65rem' }}>
                            {invoice.id}
                          </Typography>
                          {invoice.status && (
                            <Chip
                              label={invoice.status}
                              size="small"
                              sx={{
                                fontSize: '0.6rem',
                                height: 18,
                                backgroundColor: invoice.status === 'draft' ? '#fef3c7' : '#d1fae5',
                                color: invoice.status === 'draft' ? '#92400e' : '#065f46',
                                fontWeight: 500,
                              }}
                            />
                          )}
                        </Box>

                        {/* Amount */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                          <Typography variant="caption" color="#64748b">Amount Due</Typography>
                          <Typography variant="body2" fontWeight={600} color="#1e293b">
                            {invoice.amountRemaining != null ? formatCurrency(invoice.amountRemaining) : '—'}
                          </Typography>
                        </Box>

                        {invoice.amountPaid != null && invoice.amountPaid > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                            <Typography variant="caption" color="#64748b">Amount Paid</Typography>
                            <Typography variant="caption" fontWeight={500} color="#059669">
                              {formatCurrency(invoice.amountPaid)}
                            </Typography>
                          </Box>
                        )}

                        {/* Period */}
                        {(invoice.periodStart || invoice.periodEnd) && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                            <Typography variant="caption" color="#64748b">Period</Typography>
                            <Typography variant="caption" fontWeight={500}>
                              {invoice.periodStart && formatDate(invoice.periodStart)}
                              {invoice.periodStart && invoice.periodEnd && ' - '}
                              {invoice.periodEnd && formatDate(invoice.periodEnd)}
                            </Typography>
                          </Box>
                        )}

                        {/* Subscription ID */}
                        {invoice.subscriptionId && (
                          <Box sx={{ mt: 1, pt: 1, borderTop: '1px dashed #e2e8f0' }}>
                            <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#94a3b8', fontSize: '0.6rem' }}>
                              Sub: {invoice.subscriptionId}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Paper>
          </Box>

          {/* Right Content - Usage Records */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
        {(loading || subscriptionLoading) && meterUsage.length === 0 ? (
          <MeterLoadingAnimation />
        ) : error ? (
          <Alert severity="error" sx={{ borderRadius: 2 }}>
            Error loading usage data: {error.message}
          </Alert>
        ) : meterUsage.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              borderRadius: 2,
              border: '1px solid #e2e8f0',
              backgroundColor: 'white',
              textAlign: 'center',
            }}
          >
            <SpeedIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 2 }} />
            <Typography variant="h6" color="#475569" gutterBottom>
              No Usage Data
            </Typography>
            <Typography variant="body2" color="#64748b">
              No metered usage records found for this user.
            </Typography>
          </Paper>
        ) : (
          <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {groupedByDay.map((group) => (
                <Accordion
                  key={group.date}
                  defaultExpanded={false}
                  disableGutters
                  elevation={0}
                  sx={{
                    border: '1px solid #e2e8f0',
                    borderRadius: '12px !important',
                    backgroundColor: 'white',
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': { margin: 0 },
                    overflow: 'hidden',
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon sx={{ color: '#94a3b8' }} />}
                    sx={{
                      backgroundColor: '#f8fafc',
                      minHeight: 56,
                      '& .MuiAccordionSummary-content': { margin: '12px 0' },
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%', pr: 2 }}>
                      <CalendarTodayIcon sx={{ color: '#6366f1', fontSize: 20 }} />
                      <Typography variant="subtitle1" fontWeight={600} color="#1e293b">
                        {formatDate(group.date)}
                      </Typography>
                      <Chip
                        label={`${group.records.length} record${group.records.length !== 1 ? 's' : ''}`}
                        size="small"
                        sx={{
                          fontSize: '0.75rem',
                          backgroundColor: '#e2e8f0',
                          color: '#475569',
                          fontWeight: 500,
                        }}
                      />
                      {meterEventsByDate[group.date] && meterEventsByDate[group.date].length > 0 && (
                        <Chip
                          icon={<ReceiptLongIcon sx={{ fontSize: 14 }} />}
                          label={`${meterEventsByDate[group.date].length} event${meterEventsByDate[group.date].length !== 1 ? 's' : ''}`}
                          size="small"
                          onClick={(e) => handleViewMeterEvents(group.date, e)}
                          sx={{
                            fontSize: '0.75rem',
                            backgroundColor: '#fef3c7',
                            color: '#92400e',
                            fontWeight: 500,
                            cursor: 'pointer',
                            '&:hover': {
                              backgroundColor: '#fde68a',
                            },
                            '& .MuiChip-icon': {
                              color: '#92400e',
                            },
                          }}
                        />
                      )}
                      <Box sx={{ flex: 1 }} />
                      <Typography variant="body2" fontWeight={600} color="#059669">
                        {formatValue(group.totalValue.toString())}
                      </Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                            <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Master Portfolio</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Type</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#475569', textAlign: 'right' }}>Value</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Created At</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Metadata</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: '#475569', width: 60 }}></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {group.records.map((record) => {
                            const masterPortfolio = record.autoPilotSettings?.masterPortfolio?.autoPilotMasterPortfolio;
                            const slavePortfolio = record.autoPilotSettings?.slavePortfolio;
                            const profileImage = masterPortfolio?.profileImageUrl || masterPortfolio?.profileImageLargeUrl;
                            const isClickable = record.entityType === METER_USAGE_ENTITY_TYPE.AUTO_PILOT_SETTINGS;

                            return (
                              <TableRow
                                key={record.meterUsageKey}
                                sx={{
                                  '&:last-child td': { borderBottom: 0 },
                                  '&:hover': { backgroundColor: '#f8fafc' },
                                }}
                              >
                                <TableCell>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1.5,
                                      cursor: isClickable ? 'pointer' : 'default',
                                      '&:hover': isClickable ? {
                                        '& .master-title': { color: '#6366f1' },
                                      } : {},
                                    }}
                                    onClick={() => {
                                      if (isClickable) {
                                        navigate(`/pilot-details/${record.entityKey}`);
                                      }
                                    }}
                                  >
                                    <Avatar
                                      src={profileImage || undefined}
                                      sx={{
                                        width: 36,
                                        height: 36,
                                        bgcolor: '#e2e8f0',
                                      }}
                                    >
                                      {!profileImage && <PersonIcon sx={{ color: '#94a3b8', fontSize: 20 }} />}
                                    </Avatar>
                                    <Box>
                                      <Typography
                                        className="master-title"
                                        variant="body2"
                                        fontWeight={500}
                                        color="#1e293b"
                                        sx={{ transition: 'color 0.15s' }}
                                      >
                                        {masterPortfolio?.title || 'Unknown'}
                                      </Typography>
                                      {slavePortfolio?.brokerName && (
                                        <Typography variant="caption" color="#64748b" sx={{ display: 'block' }}>
                                          {slavePortfolio.brokerName}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={record.type}
                                    size="small"
                                    sx={{
                                      fontSize: '0.7rem',
                                      backgroundColor: '#dbeafe',
                                      color: '#1e40af',
                                      fontWeight: 500,
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" fontWeight={600} color="#059669">
                                    {formatValue(record.value)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="#64748b" sx={{ fontSize: '0.8rem' }}>
                                    {formatDateTime(record.createdAt)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  {record.metadata ? (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontFamily: 'monospace',
                                        fontSize: '0.75rem',
                                        color: '#64748b',
                                        maxWidth: 200,
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                      }}
                                      title={JSON.stringify(record.metadata)}
                                    >
                                      {JSON.stringify(record.metadata)}
                                    </Typography>
                                  ) : (
                                    <Typography variant="body2" color="#94a3b8" sx={{ fontStyle: 'italic' }}>
                                      —
                                    </Typography>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditClick(record)}
                                    sx={{
                                      color: '#64748b',
                                      '&:hover': {
                                        color: '#6366f1',
                                        backgroundColor: 'rgba(99, 102, 241, 0.08)',
                                      },
                                    }}
                                  >
                                    <EditIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </>
        )}
          </Box>
        </Box>
      </Box>

      {/* Edit Modal */}
      <Dialog
        open={editModalOpen}
        onClose={handleCancelEdit}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Edit Meter Usage
          </Typography>
          {editingRecord && (
            <Typography variant="caption" color="#64748b">
              Record #{editingRecord.meterUsageKey}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
              {editError}
            </Alert>
          )}
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="#64748b" sx={{ mb: 1 }}>
              Current Value: <strong>{formatValue(editingRecord?.value || '0')}</strong>
            </Typography>
            <TextField
              label="New Value"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              fullWidth
              size="small"
              type="number"
              inputProps={{ step: 'any' }}
              sx={{ mt: 1 }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelEdit}
            sx={{ textTransform: 'none', color: '#64748b' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditSubmit}
            variant="contained"
            disabled={!editValue || editValue === editingRecord?.value}
            sx={{
              textTransform: 'none',
              backgroundColor: '#6366f1',
              '&:hover': { backgroundColor: '#4f46e5' },
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Modal */}
      <Dialog
        open={confirmModalOpen}
        onClose={handleCancelConfirm}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h6" fontWeight={600}>
            Confirm Edit
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 1 }}>
            Are you sure you want to update this meter usage record?
          </Alert>
          <Box sx={{
            backgroundColor: '#f8fafc',
            borderRadius: 1,
            p: 2,
            border: '1px solid #e2e8f0',
          }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="#64748b">Record ID</Typography>
              <Typography variant="body2" fontWeight={500}>#{editingRecord?.meterUsageKey}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2" color="#64748b">Current Value</Typography>
              <Typography variant="body2" fontWeight={500}>{formatValue(editingRecord?.value || '0')}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="#64748b">New Value</Typography>
              <Typography variant="body2" fontWeight={600} color="#059669">{formatValue(editValue)}</Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCancelConfirm}
            disabled={editLoading}
            sx={{ textTransform: 'none', color: '#64748b' }}
          >
            Back
          </Button>
          <Button
            onClick={handleConfirmEdit}
            variant="contained"
            disabled={editLoading}
            sx={{
              textTransform: 'none',
              backgroundColor: '#dc2626',
              '&:hover': { backgroundColor: '#b91c1c' },
            }}
          >
            {editLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Confirm Edit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Meter Events Modal */}
      <Dialog
        open={meterEventsModalOpen}
        onClose={() => setMeterEventsModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <ReceiptLongIcon sx={{ color: '#92400e', fontSize: 24 }} />
              <Typography variant="h6" fontWeight={600}>
                Stripe Meter Events
              </Typography>
            </Box>
            {selectedDateForEvents && (
              <Typography variant="body2" color="#64748b" sx={{ mt: 0.5 }}>
                {formatDate(selectedDateForEvents)} ({selectedDateEvents.length} event{selectedDateEvents.length !== 1 ? 's' : ''})
              </Typography>
            )}
          </Box>
          <IconButton
            onClick={() => setMeterEventsModalOpen(false)}
            size="small"
            sx={{ color: '#64748b' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {selectedDateEvents.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="#64748b">
                No meter events found for this date.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Event Name</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569', textAlign: 'right' }}>Value</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Usage For Date</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Stripe Customer</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Sent to Stripe</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#475569' }}>Identifier</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedDateEvents.map((event) => (
                    <TableRow
                      key={event.identifier}
                      sx={{
                        '&:last-child td': { borderBottom: 0 },
                        '&:hover': { backgroundColor: '#f8fafc' },
                      }}
                    >
                      <TableCell>
                        <Typography variant="body2" fontWeight={500} color="#1e293b">
                          {event.eventName}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={600} color="#059669">
                          {formatValue(event.value)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#64748b', fontSize: '0.75rem' }}>
                          {event.usageForDate || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: '#64748b' }}>
                          {event.stripeCustomerId}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {event.sentToStripeAt ? (
                          <Typography variant="body2" color="#64748b" sx={{ fontSize: '0.75rem' }}>
                            {formatDateTimeWithTz(event.sentToStripeAt)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="#94a3b8" sx={{ fontStyle: 'italic' }}>
                            Not sent
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="caption"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.7rem',
                            color: '#64748b',
                            maxWidth: 150,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                          }}
                          title={event.identifier}
                        >
                          {event.identifier}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #e2e8f0' }}>
          <Button
            onClick={() => setMeterEventsModalOpen(false)}
            sx={{ textTransform: 'none', color: '#64748b' }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Usage Modal */}
      <Dialog
        open={reportModalOpen}
        onClose={handleCloseReportModal}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <SendIcon sx={{ color: '#6366f1', fontSize: 24 }} />
            <Typography variant="h6" fontWeight={600}>
              Report Current Usage
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {reportSuccess ? (
            <Alert severity="success" sx={{ borderRadius: 1 }}>
              Usage reported successfully to Stripe!
            </Alert>
          ) : (
            <>
              {reportError && (
                <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }}>
                  {reportError}
                </Alert>
              )}
              <Alert severity="info" sx={{ mb: 2, borderRadius: 1 }}>
                This will report the current metered usage to Stripe for billing.
              </Alert>
              <Box sx={{
                backgroundColor: '#f8fafc',
                borderRadius: 1,
                p: 2,
                border: '1px solid #e2e8f0',
              }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="#64748b">Subscription</Typography>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace', fontWeight: 500 }}>
                    {meteredSubscriptionId}
                  </Typography>
                </Box>
                {billableValue != null && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="#64748b">Billable Value</Typography>
                    <Typography variant="body2" fontWeight={600} color="#059669">
                      ${billableValue.toLocaleString()}
                    </Typography>
                  </Box>
                )}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={handleCloseReportModal}
            sx={{ textTransform: 'none', color: '#64748b' }}
          >
            {reportSuccess ? 'Close' : 'Cancel'}
          </Button>
          {!reportSuccess && (
            <Button
              onClick={handleReportUsage}
              variant="contained"
              disabled={reportLoading}
              sx={{
                textTransform: 'none',
                backgroundColor: '#6366f1',
                '&:hover': { backgroundColor: '#4f46e5' },
              }}
            >
              {reportLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Confirm Report'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};
